import { type FC } from 'react';

import { StepIndicatorRoot, StepIndicatorSegment, StepIndicatorTrack } from './styled';

export interface IStepIndicatorStep {
	id: number;
	title: string;
	isCompleted: boolean;
}

interface IStepIndicatorProps {
	steps: IStepIndicatorStep[];
	currentStepId: number;
	maxReachedStepId?: number;
	onStepClick?: (stepId: number) => void;
}

const StepIndicator: FC<IStepIndicatorProps> = ({ steps, currentStepId, maxReachedStepId, onStepClick }) => {
	const reachable = Math.max(currentStepId, maxReachedStepId ?? currentStepId);
	const getState = (step: IStepIndicatorStep): 'completed' | 'current' | 'upcoming' => {
		if (step.id === currentStepId) return 'current';
		if (step.id < currentStepId || step.id <= reachable || step.isCompleted) return 'completed';
		return 'upcoming';
	};

	return (
		<StepIndicatorRoot
			role='navigation'
			aria-label='Form progress'
		>
			<StepIndicatorTrack>
				{steps.map((step) => {
					const state = getState(step);
					const isClickable = state !== 'upcoming' && typeof onStepClick === 'function';
					return (
						<StepIndicatorSegment
							key={step.id}
							state={state}
							type='button'
							aria-current={state === 'current' ? 'step' : undefined}
							aria-label={`Step ${step.id}: ${step.title}`}
							disabled={!isClickable}
							onClick={() => {
								if (isClickable) onStepClick?.(step.id);
							}}
						>
							<div className='bar' />
							<div className='meta'>
								<span className={`num ${state}`}>{state === 'completed' ? '✓' : step.id}</span>
								<span className='label'>{step.title}</span>
							</div>
						</StepIndicatorSegment>
					);
				})}
			</StepIndicatorTrack>
		</StepIndicatorRoot>
	);
};

export default StepIndicator;
